import { callApi } from '../api';
import { esc, toast } from '../ui';

const DEFAULTS = {
  systemName:            'ระบบ HR Ticket',
  primaryColor:          '#1D4ED8',
  triggerKeywords:       'แจ้งเรื่อง,hr ticket',
  welcomeTitle:          'ระบบ HR Ticket',
  welcomeSubtitle:       'ยินดีต้อนรับครับ',
  welcomeBody:           'กรุณากรอกข้อมูลในรูปแบบนี้ครับ',
  infoFormat:            'รหัสพนักงาน / ชื่อ-นามสกุล / สาขา',
  infoExample:           'EMP001 / สมชาย ใจดี / สาขาลาดพร้าว',
  confirmColor:          '#15803D',
  confirmTitle:          'ยืนยันข้อมูล',
  confirmSubtitle:       'ข้อมูลถูกต้องใช่ไหมครับ?',
  menuTitle:             'เลือกประเภทเรื่อง',
  menuSubtitle:          'กรุณาเลือกหัวข้อที่ต้องการแจ้ง',
  detailColor:           '#C2410C',
  detailTitle:           'กรอกรายละเอียด',
  detailBody:            'กรุณาอธิบายรายละเอียดที่ต้องการแจ้งครับ',
  detailHint:            'ระบุให้ครบ เช่น วันที่ / จำนวน / เหตุผล เพื่อให้ HR ดำเนินการได้เร็วขึ้นครับ',
  detailExample:         '',
  ticketColor:           '#15803D',
  ticketTitle:           'รับเรื่องแล้วครับ',
  ticketFooter:          'ฝ่าย HR จะแจ้งกลับทาง LINE นี้ครับ',
  btnStatus:             'ตรวจสอบสถานะ',
  btnNewTicket:          'ยื่น Ticket ใหม่',
  statusNewColor:        '#1D4ED8',
  statusProcessingColor: '#C2410C',
  statusDoneColor:       '#15803D',
};

type CfgKey = keyof typeof DEFAULTS;

let _topicColors: Record<string, string> = {};

function g(cfg: Record<string, unknown>, key: CfgKey): string {
  return String(cfg[key] ?? DEFAULTS[key]);
}

function inpEl(id: string) { return document.getElementById(id) as HTMLInputElement | null; }

export function syncLineColor(key: string): void {
  const hex = inpEl(`lc-${key}Hex`)?.value.trim() ?? '';
  if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
    const picker = inpEl(`lc-${key}`);
    if (picker) picker.value = hex;
  }
}

function colorRow(key: string, label: string, val: string): string {
  const v = esc(val);
  return `
    <div class="line-cfg-field">
      <label class="line-cfg-label">${label}</label>
      <div class="cfg-color-wrap">
        <input type="color" id="lc-${key}" value="${v}">
        <input type="text" id="lc-${key}Hex" value="${v}" maxlength="7" class="cfg-hex-inp"
          oninput="syncLineColor('${key}')">
      </div>
    </div>`;
}

function textRow(key: string, label: string, val: string, hint = ''): string {
  return `
    <div class="line-cfg-field">
      <label class="line-cfg-label">${label}${hint ? `<span class="cfg-hint"> — ${hint}</span>` : ''}</label>
      <input type="text" id="lc-${key}" value="${esc(val)}" placeholder="${esc(DEFAULTS[key as CfgKey] ?? '')}">
    </div>`;
}

function section(icon: string, title: string, body: string): string {
  return `
    <div class="line-cfg-card">
      <div class="line-cfg-card-title"><i class="ti ${icon}"></i> ${title}</div>
      <div class="line-cfg-fields">${body}</div>
    </div>`;
}

function topicColorsDisplay(): string {
  const entries = Object.entries(_topicColors);
  const badges = entries.length
    ? entries.map(([name, color]) =>
        `<span style="display:inline-flex;align-items:center;gap:5px;background:${esc(color)}22;border:1px solid ${esc(color)}55;border-radius:6px;padding:3px 9px;font-size:12px">
          <span style="width:9px;height:9px;border-radius:50%;background:${esc(color)};flex-shrink:0"></span>${esc(name)}
        </span>`
      ).join('')
    : `<span style="color:var(--muted);font-size:12px">ยังไม่มีหัวข้อ — เพิ่มได้ที่ตั้งค่า › จัดการหัวข้อ</span>`;
  return `
    <div class="line-cfg-field">
      <label class="line-cfg-label">สีปุ่มหมวดหมู่<span class="cfg-hint"> — แก้ไขสีได้ที่ตั้งค่า › จัดการหัวข้อ</span></label>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:4px">${badges}</div>
    </div>`;
}

function renderForm(cfg: Record<string, unknown>): void {
  const body = document.getElementById('lineConfigBody');
  if (!body) return;

  body.innerHTML = [
    section('ti-settings-2', 'ทั่วไป', [
      textRow('systemName',      'ชื่อระบบ',                      g(cfg, 'systemName')),
      textRow('triggerKeywords', 'คีย์เวิร์ดเริ่มต้น',            g(cfg, 'triggerKeywords'), 'คั่นด้วยลูกน้ำ'),
      colorRow('primaryColor',   'สีหลัก LINE Bot',               g(cfg, 'primaryColor')),
    ].join('')),

    section('ti-message', 'ข้อความต้อนรับ', [
      textRow('welcomeTitle',    'หัวข้อ',                        g(cfg, 'welcomeTitle')),
      textRow('welcomeSubtitle', 'ข้อความรอง',                   g(cfg, 'welcomeSubtitle')),
      textRow('welcomeBody',     'เนื้อหา',                       g(cfg, 'welcomeBody')),
      textRow('infoFormat',      'รูปแบบข้อมูลพนักงาน',          g(cfg, 'infoFormat')),
      textRow('infoExample',     'ตัวอย่าง',                      g(cfg, 'infoExample')),
    ].join('')),

    section('ti-circle-check-filled', 'ยืนยันข้อมูล', [
      colorRow('confirmColor',   'สี header',                     g(cfg, 'confirmColor')),
      textRow('confirmTitle',    'หัวข้อ',                        g(cfg, 'confirmTitle')),
      textRow('confirmSubtitle', 'ข้อความรอง',                   g(cfg, 'confirmSubtitle')),
    ].join('')),

    section('ti-category', 'เมนูเลือกหัวข้อ (หมวดหมู่)', [
      textRow('menuTitle',       'หัวข้อเมนู',                    g(cfg, 'menuTitle')),
      textRow('menuSubtitle',    'ข้อความรอง',                   g(cfg, 'menuSubtitle'), 'แสดงใต้หัวข้อเมนู'),
      topicColorsDisplay(),
    ].join('')),

    section('ti-forms', 'กรอกรายละเอียด', [
      colorRow('detailColor',    'สี header',                     g(cfg, 'detailColor')),
      textRow('detailTitle',     'หัวข้อ',                        g(cfg, 'detailTitle')),
      textRow('detailBody',      'เนื้อหา',                       g(cfg, 'detailBody')),
      textRow('detailHint',      'คำใบ้',                         g(cfg, 'detailHint')),
      textRow('detailExample',   'ตัวอย่าง',                      g(cfg, 'detailExample'), 'เว้นว่างเพื่อไม่แสดง'),
    ].join('')),

    section('ti-ticket', 'เปิด Ticket สำเร็จ', [
      colorRow('ticketColor',    'สี header',                     g(cfg, 'ticketColor')),
      textRow('ticketTitle',     'หัวข้อ',                        g(cfg, 'ticketTitle')),
      textRow('ticketFooter',    'ข้อความท้าย',                  g(cfg, 'ticketFooter')),
    ].join('')),

    section('ti-hand-click', 'ปุ่ม', [
      textRow('btnStatus',       'ปุ่มตรวจสอบสถานะ',             g(cfg, 'btnStatus')),
      textRow('btnNewTicket',    'ปุ่มยื่น Ticket ใหม่',         g(cfg, 'btnNewTicket')),
    ].join('')),

    section('ti-palette', 'สีสถานะ Ticket', [
      colorRow('statusNewColor',        'ใหม่ / รอดำเนินการ',    g(cfg, 'statusNewColor')),
      colorRow('statusProcessingColor', 'กำลังดำเนินการ',        g(cfg, 'statusProcessingColor')),
      colorRow('statusDoneColor',       'เสร็จสิ้น',             g(cfg, 'statusDoneColor')),
    ].join('')),
  ].join('');
}

function readValue(key: string): string {
  return inpEl(`lc-${key}`)?.value.trim() ?? '';
}

export async function loadLineConfig(): Promise<void> {
  const body = document.getElementById('lineConfigBody');
  if (!body) return;
  body.innerHTML = '<div class="empty"><i class="ti ti-loader spin"></i>กำลังโหลด...</div>';

  const res = await callApi({ action: 'getLineConfig' });
  const cfg = (res.ok ? res['config'] : {}) as Record<string, unknown>;
  _topicColors = (cfg['topicColors'] as Record<string, string> | undefined) ?? {};
  renderForm(cfg);
}

export async function saveLineConfig(): Promise<void> {
  const keys: CfgKey[] = [
    'systemName', 'primaryColor', 'triggerKeywords',
    'welcomeTitle', 'welcomeSubtitle', 'welcomeBody', 'infoFormat', 'infoExample',
    'confirmColor', 'confirmTitle', 'confirmSubtitle',
    'menuTitle', 'menuSubtitle',
    'detailColor', 'detailTitle', 'detailBody', 'detailHint', 'detailExample',
    'ticketColor', 'ticketTitle', 'ticketFooter',
    'btnStatus', 'btnNewTicket',
    'statusNewColor', 'statusProcessingColor', 'statusDoneColor',
  ];

  const config: Record<string, unknown> = { topicColors: _topicColors };
  for (const k of keys) {
    const val = readValue(k);
    config[k] = val || DEFAULTS[k];
  }

  const res = await callApi({ action: 'saveLineConfig', config });
  if (res.ok) toast('success', 'บันทึก LINE Config แล้ว');
  else toast('error', res.error ?? 'บันทึกไม่สำเร็จ');
}
